import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { User, CreditCard, Shield, Users, ChevronRight, LogOut, Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import PlanBadge from '../../components/ui/PlanBadge'

const COGNITO_REGION = 'us-east-1'
const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`

async function cognitoChangePassword(accessToken: string, oldPassword: string, newPassword: string): Promise<void> {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const code = err.__type ?? ''
    if (code.includes('NotAuthorizedException')) throw new Error('La contraseña actual es incorrecta.')
    if (code.includes('InvalidPasswordException')) throw new Error(err.message ?? 'La nueva contraseña no cumple los requisitos.')
    throw new Error(err.message ?? 'Error al cambiar la contraseña.')
  }
}

export default function SettingsPage() {
  const { user, logout, accessToken } = useAuthStore()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState(user?.name ?? '')
  const [saved, setSaved] = useState(false)

  // Password change state
  const [pwOpen, setPwOpen] = useState(false)
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState(false)

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000) }, 500)
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError('')
    if (newPw.length < 8) { setPwError('La nueva contraseña debe tener al menos 8 caracteres.'); return }
    if (newPw !== confirmPw) { setPwError('Las contraseñas no coinciden.'); return }
    if (!accessToken) { setPwError('Sesión expirada. Vuelve a iniciar sesión.'); return }
    setPwSaving(true)
    try {
      await cognitoChangePassword(accessToken, oldPw, newPw)
      setPwSuccess(true)
      setOldPw(''); setNewPw(''); setConfirmPw('')
      setTimeout(() => { setPwSuccess(false); setPwOpen(false) }, 3000)
    } catch (err: unknown) {
      setPwError(err instanceof Error ? err.message : 'Error al cambiar la contraseña.')
    } finally {
      setPwSaving(false)
    }
  }

  const handleLogout = () => { logout(); navigate('/login') }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Configuración</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>Gestiona tu cuenta y preferencias</p>
      </div>

      {/* Profile */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <User className="w-4 h-4" style={{ color: '#818CF8' }} />
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Perfil</h2>
        </div>
        <form onSubmit={handleSaveName} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#64748B' }}>Nombre</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#F1F5F9',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(79,70,229,0.5)' }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)' }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#64748B' }}>Correo electrónico</label>
            <input
              type="email"
              value={user?.email ?? ''}
              readOnly
              className="w-full px-3 py-2 rounded-xl text-sm"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                color: '#475569',
                cursor: 'default',
              }}
            />
            <p className="text-xs mt-1" style={{ color: '#334155' }}>
              El correo es administrado por tu proveedor de autenticación
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving || name === user?.name}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saving ? 'Guardando...' : saved ? 'Guardado ✓' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>

      {/* Plan */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="w-4 h-4" style={{ color: '#818CF8' }} />
            <div>
              <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Plan</h2>
              <div className="mt-1">
                <PlanBadge plan={user?.plan ?? 'free'} size="sm" />
              </div>
            </div>
          </div>
          <Link
            to="/settings/billing"
            className="flex items-center gap-1.5 text-sm transition-colors"
            style={{ color: '#818CF8' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#A5B4FC' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#818CF8' }}
          >
            Gestionar <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Security */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4" style={{ color: '#818CF8' }} />
            <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Seguridad</h2>
          </div>
          <button
            onClick={() => { setPwOpen((o) => !o); setPwError(''); setPwSuccess(false) }}
            className="text-sm transition-colors"
            style={{ color: '#818CF8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#A5B4FC' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#818CF8' }}
          >
            {pwOpen ? 'Cancelar' : 'Cambiar contraseña →'}
          </button>
        </div>

        {!pwOpen && (
          <p className="text-sm" style={{ color: '#64748B' }}>
            Mantén tu cuenta segura usando una contraseña única y robusta.
          </p>
        )}

        {pwOpen && (
          <form onSubmit={handleChangePassword} className="space-y-3 mt-2">
            {/* Current password */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#64748B' }}>Contraseña actual</label>
              <div className="relative">
                <input
                  type={showOld ? 'text' : 'password'}
                  value={oldPw}
                  onChange={(e) => setOldPw(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-3 py-2 pr-9 rounded-xl text-sm outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#F1F5F9' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(79,70,229,0.5)' }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)' }}
                />
                <button type="button" onClick={() => setShowOld((v) => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}>
                  {showOld ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#64748B' }}>Nueva contraseña</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-3 py-2 pr-9 rounded-xl text-sm outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#F1F5F9' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(79,70,229,0.5)' }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)' }}
                />
                <button type="button" onClick={() => setShowNew((v) => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}>
                  {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Confirm */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#64748B' }}>Confirmar nueva contraseña</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-3 py-2 rounded-xl text-sm outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#F1F5F9' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(79,70,229,0.5)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)' }}
              />
            </div>

            {pwError && <p className="text-xs" style={{ color: '#F87171' }}>{pwError}</p>}
            {pwSuccess && <p className="text-xs" style={{ color: '#34D399' }}>✓ Contraseña actualizada correctamente.</p>}

            <button
              type="submit"
              disabled={pwSaving || !oldPw || !newPw || !confirmPw}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {pwSaving ? 'Actualizando...' : 'Actualizar contraseña'}
            </button>
          </form>
        )}
      </div>

      {/* Family */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-4 h-4" style={{ color: '#818CF8' }} />
            <div>
              <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Familia</h2>
              <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                {user?.role === 'parent'
                  ? 'Vinculá a tus hijos para ver su progreso'
                  : 'Vinculá tu cuenta con tu padre o tutor'}
              </p>
            </div>
          </div>
          <Link
            to="/settings/family"
            className="flex items-center gap-1.5 text-sm"
            style={{ color: '#818CF8' }}
          >
            {user?.role === 'parent' ? 'Gestionar' : 'Vincular'} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Logout */}
      <div className="glass-card p-5">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: '#F43F5E' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#FB7185' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#F43F5E' }}
        >
          <LogOut className="w-4 h-4" />
          Cerrar sesión
        </button>
      </div>
    </div>
  )
}
